/**
 * Hook React para resolver o role do usuário autenticado (E2.F4). // ux-neutrality:allow doc-comment
 *
 * Lê de GET /api/perfil (campo `role`) com memoização em módulo: o // ux-neutrality:allow doc-comment
 * primeiro componente que renderizar dispara o fetch; os demais // ux-neutrality:allow doc-comment
 * reaproveitam a mesma Promise. Em caso de erro (auth ou rede), expõe // ux-neutrality:allow doc-comment
 * `role=null` e `error` — o caller decide se redireciona ou exibe // ux-neutrality:allow doc-comment
 * estado vago. // ux-neutrality:allow doc-comment
 *
 * Padrão de uso: // ux-neutrality:allow doc-comment
 *   const { role, loading, error } = useCurrentRole(); // ux-neutrality:allow doc-comment
 *   if (loading) return <Skeleton />; // ux-neutrality:allow doc-comment
 *   if (role !== 'owner') return <NoAccess />; // ux-neutrality:allow doc-comment
 *
 * Reset: chame `resetCurrentRoleCache()` ao logout ou após mudança de // ux-neutrality:allow doc-comment
 * identidade — a próxima leitura refaz o fetch. // ux-neutrality:allow doc-comment
 */
import { useEffect, useState } from 'react';
import { useApiClient } from '@/lib/api';
import { useSessionStore } from '@/stores/session';
import { getPerfil, PerfilError } from '@/lib/perfil';
import type { Ambiente, PerfilRole } from '@/lib/auth';

interface CachedRole {
  promise: Promise<PerfilRole | null> | null;
  value: PerfilRole | null;
  userId: string | null;
  ambiente: Ambiente | null;
  requireReviewerApproval: boolean;
  /** E50.F6: sessão do escritório-plataforma (exibe a esteira do portal). */
  isPlatform: boolean;
  /** E65.F3 (ADR-0069): troca de senha pendente — o servidor recusa o produto. */
  senhaProvisoria: boolean;
  errored: boolean;
}

const _cache: CachedRole = {
  promise: null,
  value: null,
  userId: null,
  ambiente: null,
  requireReviewerApproval: false,
  isPlatform: false,
  senhaProvisoria: false,
  errored: false,
};

export function resetCurrentRoleCache(): void {
  _cache.promise = null;
  _cache.value = null;
  _cache.userId = null;
  _cache.ambiente = null;
  _cache.requireReviewerApproval = false;
  _cache.isPlatform = false;
  _cache.senhaProvisoria = false;
  _cache.errored = false;
}

export interface UseCurrentRoleResult {
  role: PerfilRole | null;
  /**
   * ID do usuário autenticado. Era o predicado do aviso de auto-homologação
   * (E23.F7); desde a ADR-0068/E63.F1.C13 esse aviso vem do servidor, e este
   * campo só serve de fallback quando a pré-checagem não responde.
   */
  userId: string | null;
  /**
   * E50.F2/ADR-0059: área "casa" do usuário (`interno` = perito;
   * `escritorio`/`banco` = portal). `null` enquanto o perfil não resolve.
   */
  ambiente: Ambiente | null;
  /**
   * E34.F1/ADR-0046: flag de config — exige homologação por revisor para baixar
   * o laudo. `false` (default) esconde o painel e libera o download.
   */
  requireReviewerApproval: boolean;
  /**
   * E50.F6: `true` só para o escritório-plataforma — exibe a esteira do portal
   * na área do perito (a autorização real é do servidor).
   */
  isPlatform: boolean;
  /**
   * E65.F3 (ADR-0069): a senha vigente foi definida por terceiro e a troca está
   * pendente. Quem recusa é o SERVIDOR; a tela usa isto só para levar a pessoa
   * à troca em vez de deixá-la bater em 403 sem explicação.
   */
  senhaProvisoria: boolean;
  loading: boolean;
  error: boolean;
}

export function useCurrentRole(): UseCurrentRoleResult {
  const apiClient = useApiClient();
  const token = useSessionStore((s) => s.token); // ux-neutrality:allow auth-token
  const [snapshot, setSnapshot] = useState<UseCurrentRoleResult>({
    role: _cache.value,
    userId: _cache.userId,
    ambiente: _cache.ambiente,
    requireReviewerApproval: _cache.requireReviewerApproval,
    isPlatform: _cache.isPlatform,
    senhaProvisoria: _cache.senhaProvisoria,
    loading: _cache.value === null && !_cache.errored,
    error: _cache.errored,
  });

  const hasSession = token !== null; // ux-neutrality:allow auth-token

  useEffect(() => {
    if (!hasSession) {
      // Sem sessão: nada para resolver, mas zere o estado se houver stale.
      resetCurrentRoleCache();
      setSnapshot({
        role: null,
        userId: null,
        ambiente: null,
        requireReviewerApproval: false,
        isPlatform: false,
        senhaProvisoria: false,
        loading: false,
        error: false,
      });
      return;
    }
    if (_cache.value !== null) {
      setSnapshot({
        role: _cache.value,
        userId: _cache.userId,
        ambiente: _cache.ambiente,
        requireReviewerApproval: _cache.requireReviewerApproval,
        isPlatform: _cache.isPlatform,
        senhaProvisoria: _cache.senhaProvisoria,
        loading: false,
        error: false,
      });
      return;
    }
    if (_cache.promise === null) {
      _cache.promise = (async () => {
        try {
          const perfil = await getPerfil(apiClient);
          _cache.value = perfil.role;
          _cache.userId = perfil.id;
          _cache.ambiente = perfil.ambiente;
          _cache.requireReviewerApproval = perfil.require_reviewer_approval;
          _cache.isPlatform = perfil.is_platform;
          _cache.senhaProvisoria = perfil.senha_provisoria;
          _cache.errored = false;
          return perfil.role;
        } catch (err) {
          // E70.F1: a falha é do MOMENTO (rede, 401 de sessão vencida, 5xx), não
          // da identidade. Guardar a promise rejeitada fazia o próximo guard
          // reaproveitá-la para sempre — `isPlatform=false` com `loading=false`
          // virava "HTTP 403 · Acesso restrito" até um F5 (que recria o módulo)
          // ou um logout (que zera a memória do módulo). Solta a promise: a
          // próxima montagem refaz o fetch.
          _cache.errored = true;
          _cache.promise = null;
          if (err instanceof PerfilError) {
            return null;
          }
          return null;
        }
      })();
    }
    setSnapshot({
      role: null,
      userId: null,
      ambiente: null,
      requireReviewerApproval: false,
      isPlatform: false,
      senhaProvisoria: false,
      loading: true,
      error: false,
    });
    let active = true;
    _cache.promise
      .then((value) => {
        if (!active) return;
        setSnapshot({
          role: value,
          userId: _cache.userId,
          ambiente: _cache.ambiente,
          requireReviewerApproval: _cache.requireReviewerApproval,
          isPlatform: _cache.isPlatform,
          senhaProvisoria: _cache.senhaProvisoria,
          loading: false,
          error: value === null && _cache.errored,
        });
      })
      .catch(() => {
        if (!active) return;
        setSnapshot({
          role: null,
          userId: null,
          ambiente: null,
          requireReviewerApproval: false,
          isPlatform: false,
          senhaProvisoria: false,
          loading: false,
          error: true,
        });
      });
    return () => {
      active = false;
    };
  }, [apiClient, hasSession]);

  return snapshot;
}
